/**
 * Task Utilities for Hierarchical Timeline System
 * Handles current task detection, progress calculation, and task state logic
 */

/**
 * Find the current (next actionable) subtask
 * @param {Array} subtasks - Array of subtask objects
 * @returns {Object|null} - The current subtask or null
 */
export function getCurrentSubtask(subtasks) {
  if (!subtasks || !Array.isArray(subtasks)) return null;

  // Find first incomplete, unlocked subtask
  return subtasks.find(subtask =>
    subtask.status !== 'completed' &&
    !subtask.is_locked
  ) || null;
}

/**
 * Calculate progress for a parent task
 * @param {Array} subtasks - Array of subtask objects
 * @returns {Object} - Progress data
 */
export function calculateProgress(subtasks) {
  if (!subtasks || !Array.isArray(subtasks)) {
    return { completed: 0, total: 0, percentage: 0 };
  }

  const completed = subtasks.filter(t => t.status === 'completed').length;
  const total = subtasks.length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return { completed, total, percentage };
}

/**
 * Check if a task is overdue
 * @param {Object} task - Task object with due_date
 * @returns {boolean}
 */
export function isOverdue(task) {
  if (!task?.due_date) return false;

  const dueDate = new Date(task.due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dueDate < today && task.status !== 'completed';
}

/**
 * Get days until/since due date
 * @param {Object} task - Task object with due_date
 * @returns {number} - Positive = days until, Negative = days overdue
 */
export function getDaysUntilDue(task) {
  if (!task?.due_date) return null;

  const dueDate = new Date(task.due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  const diffTime = dueDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Format due date for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date (e.g., "Oct 18", "Tomorrow", "Today")
 */
export function formatDueDate(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  today.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';

  // Format as "Oct 18"
  const options = { month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Format time estimate for display
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted duration (e.g., "~2 hrs", "~30 min")
 */
export function formatTimeEstimate(minutes) {
  if (!minutes || minutes === 0) return '';

  if (minutes < 60) {
    return `~${minutes} min`;
  }

  const hours = minutes / 60;

  // If it's a whole number of hours
  if (hours === Math.floor(hours)) {
    return `~${hours} hr${hours !== 1 ? 's' : ''}`;
  }

  // If it has a decimal (e.g., 1.5 hours)
  return `~${hours.toFixed(1)} hrs`;
}

/**
 * Get time estimate from task (prefers MVV version)
 * @param {Object} task - Task object
 * @returns {number|null} - Duration in minutes
 */
export function getTimeEstimate(task) {
  // Prefer MVV time if available
  if (task?.mvv_time_required) return task.mvv_time_required;
  if (task?.estimated_duration) return task.estimated_duration;
  return null;
}

/**
 * Check if parent task should be expanded by default
 * @param {Object} parentTask - Parent task object
 * @param {Array} subtasks - Array of subtask objects
 * @param {number} index - Position in priority list
 * @returns {boolean}
 */
export function shouldExpandByDefault(parentTask, subtasks, index) {
  // Only expand the first task if it has a current subtask
  if (index !== 0) return false;

  const currentSubtask = getCurrentSubtask(subtasks);
  return currentSubtask !== null;
}

/**
 * Check if task has current subtask (for collapsed state indicator)
 * @param {Array} subtasks - Array of subtask objects
 * @returns {boolean}
 */
export function hasCurrentSubtask(subtasks) {
  return getCurrentSubtask(subtasks) !== null;
}

/**
 * Get next subtask preview text for collapsed view
 * @param {Array} subtasks - Array of subtask objects
 * @returns {string|null}
 */
export function getNextSubtaskPreview(subtasks) {
  const current = getCurrentSubtask(subtasks);
  if (!current) return null;

  const daysUntil = getDaysUntilDue(current);
  const dateText = formatDueDate(current.due_date);

  if (daysUntil === null) {
    return `Next: ${current.title}`;
  }

  if (daysUntil < 0) {
    return `Next: ${current.title} (${Math.abs(daysUntil)} days overdue)`;
  }

  return `Next: ${current.title} (due ${dateText})`;
}

/**
 * Detect if user is in recovery mode (has overdue tasks)
 * @param {Array} tasks - Array of all tasks
 * @returns {boolean}
 */
export function isRecoveryMode(tasks) {
  if (!tasks || !Array.isArray(tasks)) return false;

  return tasks.some(task => isOverdue(task));
}

/**
 * Get recovery mode stats
 * @param {Array} tasks - Array of all tasks
 * @returns {Object} - Recovery stats
 */
export function getRecoveryStats(tasks) {
  if (!tasks || !Array.isArray(tasks)) {
    return { overdueCount: 0, thisWeekCount: 0, blockedCount: 0 };
  }

  const overdueCount = tasks.filter(task => isOverdue(task)).length;

  const thisWeekEnd = new Date();
  thisWeekEnd.setDate(thisWeekEnd.getDate() + 7);
  const thisWeekCount = tasks.filter(task => {
    if (!task.due_date || task.status === 'completed') return false;
    const dueDate = new Date(task.due_date);
    return dueDate <= thisWeekEnd;
  }).length;

  const blockedCount = tasks.filter(task => task.is_locked).length;

  return { overdueCount, thisWeekCount, blockedCount };
}

/**
 * Find foundation tasks that should be highlighted
 * @param {Array} tasks - Array of all tasks
 * @returns {Array} - Foundation tasks
 */
export function getFoundationTasks(tasks) {
  if (!tasks || !Array.isArray(tasks)) return [];

  return tasks.filter(task =>
    task.is_foundation_task &&
    task.status !== 'completed' &&
    task.unlock_count > 0
  );
}

/**
 * Get tasks that will be unlocked by completing a foundation task
 * @param {Object} foundationTask - Foundation task object
 * @param {Array} allTasks - Array of all tasks
 * @returns {Array} - Tasks that will be unlocked
 */
export function getUnlockedTasks(foundationTask, allTasks) {
  if (!foundationTask?.unlocks_task_ids || !Array.isArray(allTasks)) return [];

  return allTasks.filter(task =>
    foundationTask.unlocks_task_ids.includes(task.id)
  );
}

/**
 * Sort tasks by priority with deadline awareness
 * Priority logic (DEADLINE-AWARE):
 * - EMERGENCY/SPRINT MODE: Tasks for imminent deadline college ONLY
 * - Within urgent college: Critical tasks by due date
 * - NORMAL MODE: Standard priority (overdue, foundation, current, due date)
 *
 * @param {Array} tasks - Array of task objects
 * @param {Object} taskSubtasksMap - Map of task IDs to their subtasks
 * @param {Object} deadlineContext - Optional deadline context { phase, college_id, daysUntil }
 * @returns {Array} - Sorted tasks
 */
export function sortTasksByPriority(tasks, taskSubtasksMap = {}, deadlineContext = null) {
  if (!tasks || !Array.isArray(tasks)) return [];

  return [...tasks].sort((a, b) => {
    // DEADLINE-AWARE SORTING
    if (deadlineContext && deadlineContext.phase) {
      const phase = deadlineContext.phase;
      const urgentCollegeId = deadlineContext.college_id;

      // EMERGENCY or SPRINT MODE: Prioritize urgent college tasks heavily
      if (phase === 'EMERGENCY_7' || phase === 'SPRINT_14') {
        const aIsUrgent = a.college_id === urgentCollegeId;
        const bIsUrgent = b.college_id === urgentCollegeId;

        // Urgent college tasks come first
        if (aIsUrgent && !bIsUrgent) return -1;
        if (!aIsUrgent && bIsUrgent) return 1;

        // If both are for urgent college, sort by criticality
        if (aIsUrgent && bIsUrgent) {
          // Critical: < 7 days to complete
          const aDaysUntil = getDaysUntilDue(a);
          const bDaysUntil = getDaysUntilDue(b);

          const aIsCritical = aDaysUntil !== null && aDaysUntil < 7;
          const bIsCritical = bDaysUntil !== null && bDaysUntil < 7;

          if (aIsCritical && !bIsCritical) return -1;
          if (!aIsCritical && bIsCritical) return 1;

          // Both critical: sort by due date
          if (aIsCritical && bIsCritical && a.due_date && b.due_date) {
            return new Date(a.due_date) - new Date(b.due_date);
          }
        }

        // If both NOT urgent college, deprioritize equally
        if (!aIsUrgent && !bIsUrgent) {
          // Sort them to the end but maintain some order
          if (a.due_date && b.due_date) {
            return new Date(a.due_date) - new Date(b.due_date);
          }
          return 0;
        }
      }

      // CRUNCH MODE: Prioritize deadline schools but don't hide others
      if (phase === 'CRUNCH_30') {
        const aIsUrgent = a.college_id === urgentCollegeId;
        const bIsUrgent = b.college_id === urgentCollegeId;

        // Bonus points for urgent college, but don't exclusively filter
        if (aIsUrgent && !bIsUrgent) return -1;
        if (!aIsUrgent && bIsUrgent) return 1;
      }
    }

    // STANDARD PRIORITY LOGIC (for normal mode or within same urgency level)

    // 1. Overdue tasks first
    const aOverdue = isOverdue(a);
    const bOverdue = isOverdue(b);
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    // 2. Foundation tasks (high unlock count)
    const aUnlockCount = a.unlock_count || 0;
    const bUnlockCount = b.unlock_count || 0;
    if (aUnlockCount > bUnlockCount) return -1;
    if (aUnlockCount < bUnlockCount) return 1;

    // 3. Tasks with current subtasks
    const aHasCurrent = hasCurrentSubtask(taskSubtasksMap[a.id] || []);
    const bHasCurrent = hasCurrentSubtask(taskSubtasksMap[b.id] || []);
    if (aHasCurrent && !bHasCurrent) return -1;
    if (!aHasCurrent && bHasCurrent) return 1;

    // 4. By due date (earlier first)
    if (a.due_date && b.due_date) {
      return new Date(a.due_date) - new Date(b.due_date);
    }
    if (a.due_date) return -1;
    if (b.due_date) return 1;

    return 0;
  });
}

/**
 * Get "Next Action" - single most important thing to do right now
 * @param {Array} tasks - Array of all parent tasks
 * @param {Object} taskSubtasksMap - Map of task IDs to their subtasks
 * @param {Object} deadlineContext - Optional deadline context for smart prioritization
 * @returns {Object|null} - Next action (subtask level)
 */
export function getNextAction(tasks, taskSubtasksMap = {}, deadlineContext = null) {
  if (!tasks || !Array.isArray(tasks)) return null;

  // Sort tasks by priority (now deadline-aware)
  const sortedTasks = sortTasksByPriority(tasks, taskSubtasksMap, deadlineContext);

  // Find first task with a current subtask
  for (const task of sortedTasks) {
    const subtasks = taskSubtasksMap[task.id] || [];
    const currentSubtask = getCurrentSubtask(subtasks);

    if (currentSubtask) {
      return {
        parentTask: task,
        subtask: currentSubtask,
        isFoundation: task.is_foundation_task,
        unlockCount: task.unlock_count || 0,
        deadlinePhase: deadlineContext?.phase || 'NORMAL',
        daysUntilDeadline: deadlineContext?.daysUntil || null
      };
    }
  }

  return null;
}
